import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Download, ArrowRight } from 'lucide-react';
import Seo, { breadcrumbSchema, faqSchema } from '@/components/Seo';
import Breadcrumbs from '@/components/Breadcrumbs';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { EnquireButton, SiteVisitButton, WhatsAppButton, CallButton } from '@/components/CTAButtons';
import { SEO_PAGES } from '@/data/seoPages';
import { PRICING, PRICING_NOTES, FAQS } from '@/data/siteData';
import { trackEvent } from '@/lib/tracking';

const PricingTable = ({ filter }) => {
  const rows = filter ? PRICING.filter((p) => p.type === filter) : PRICING;
  return (
    <div className="overflow-x-auto rounded-2xl border border-line bg-white shadow-sm my-6" data-testid="seo-pricing-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="bg-palm text-ivory text-left">
            <th className="px-5 py-3.5 font-semibold">Typology</th>
            <th className="px-5 py-3.5 font-semibold">Carpet Area</th>
            <th className="px-5 py-3.5 font-semibold">Agreement Value</th>
            <th className="px-5 py-3.5 font-semibold">All-Incl. (1–10 Flr)</th>
            <th className="px-5 py-3.5 font-semibold">All-Incl. (11–20 Flr)</th>
            <th className="px-5 py-3.5 font-semibold">All-Incl. (21–30 Flr)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={i} className="border-t border-line">
              <td className="px-5 py-3.5 font-semibold text-palm">{p.type}</td>
              <td className="px-5 py-3.5">{p.carpet}</td>
              <td className="px-5 py-3.5">{p.agreement}</td>
              <td className="px-5 py-3.5 text-gold font-semibold">{p.f1}</td>
              <td className="px-5 py-3.5">{p.f2}</td>
              <td className="px-5 py-3.5">{p.f3}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SeoPage = ({ pageKey }) => {
  const page = SEO_PAGES[pageKey];
  if (!page) return <Navigate to="/" replace />;
  const jsonLd = [breadcrumbSchema([{ name: 'Home', path: '/' }, { name: page.h1, path: pageKey }])];
  if (page.faq) jsonLd.push(faqSchema(FAQS.slice(0, 6)));

  return (
    <>
      <Seo title={page.title} description={page.description} path={pageKey} jsonLd={jsonLd} />
      <section className="pt-28 md:pt-36 pb-16 md:pb-24 bg-ivory min-h-[70vh]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: page.eyebrow }]} />
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">{page.eyebrow}</p>
            <h1 className="font-display text-4xl sm:text-5xl font-medium text-palm mt-3 leading-[1.08] tracking-tight">{page.h1}</h1>
          </Reveal>

          <div className="flex flex-wrap gap-3 mt-7">
            <EnquireButton testid={`seo-${pageKey.slice(1)}-enquire-btn`} />
            <WhatsAppButton testid={`seo-${pageKey.slice(1)}-whatsapp-btn`} />
            <CallButton testid={`seo-${pageKey.slice(1)}-call-btn`} />
            <SiteVisitButton testid={`seo-${pageKey.slice(1)}-visit-btn`} className="!border-palm/25 !text-palm hover:!bg-paper" />
          </div>

          <div className="mt-12 space-y-12">
            {page.sections.map((s, i) => (
              <Reveal key={i} delay={0.05}>
                <h2 className="font-display text-2xl sm:text-3xl font-medium text-palm">{s.heading}</h2>
                {s.paragraphs?.map((p, j) => (
                  <p key={j} className="text-[15px] leading-relaxed text-ink/80 mt-4">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="mt-4 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[15px] text-ink/80 leading-relaxed">
                        <span className="h-1.5 w-1.5 rounded-full bg-gold mt-2.5 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {s.table === 'pricing' && (
                  <>
                    <PricingTable />
                    <ul className="space-y-1.5 mt-3">
                      {PRICING_NOTES.map((n) => (
                        <li key={n} className="text-xs text-muted-foreground leading-relaxed">* {n}</li>
                      ))}
                    </ul>
                  </>
                )}
                {s.table === 'pricing-1bhk' && <PricingTable filter="1 BHK" />}
                {s.table === 'pricing-2bhk' && <PricingTable filter="2 BHK" />}
                {s.brochure && (
                  <a href="/assets/palm-vista-opportunity.pdf" target="_blank" rel="noopener noreferrer"
                    data-testid="brochure-download-btn"
                    onClick={() => trackEvent('brochure_click')}
                    className="inline-flex items-center gap-2 rounded-full bg-palm text-ivory px-6 py-3 text-sm font-semibold mt-6 hover:bg-palm-2 transition-colors shadow-md">
                    <Download className="h-4 w-4 text-gold" /> Download Project Document (PDF)
                  </a>
                )}
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14 bg-paper border border-line rounded-3xl p-7">
            <h2 className="font-display text-2xl font-medium text-palm">Explore further</h2>
            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-4">
              <Link to="/provident-palm-vista-price" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Latest price <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/provident-palm-vista-floor-plan" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Floor plans <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/provident-palm-vista-amenities" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Amenities <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/provident-palm-vista-location" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Location <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/provident-palm-vista-rera" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">RERA details <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Contact <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
        </div>
      </section>
      <CTAPanel />
    </>
  );
};

export default SeoPage;
